import { TourConfig, TourStep } from '../../types';
import { HABITAT_ONBOARDING_OUTAGE_ID } from './ids';
import { VIDEO_DEMO_SRC } from './media';
import { habitatTailSteps, TAIL_STEP_IDS } from './tail';
import { TOUR_TARGETS } from './targets';

const STEP = {
  WELCOME: '1e-welcome',
  VM: 'vm-welcome',
  FEED_BRANCH: '12-branch',
  FEED_FOLLOWS: '12-feed-follows',
  FEED_NO_FOLLOW: '12b-feed-no-follow',
  FOLLOW_ALT: '9b-follow-alt',
} as const;

/**
 * The camera is down, or the habitat has no schedule at all. Unlike the asleep scenario there is
 * no stream window to quote and no promise about when they will be back, so the copy leads with
 * who lives here and leans on the community's Activity Feed for "where were they last seen".
 */
const steps: TourStep[] = [
  {
    id: STEP.WELCOME,
    analytics: { stepNumber: 1, section: 'Welcome (outage)' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble: 'Hey there, come on in! This habitat is located in {{ctx.habitat.country}}. Ready to explore?',
        buttons: [
          { label: 'Show me who lives here', targetStepId: TAIL_STEP_IDS.MEET_LEARN },
          { label: 'Show me the cameras', targetStepId: STEP.VM },
        ],
      },
      spotlight: { target: TOUR_TARGETS.HABITAT_TABS, scrollIntoView: true },
      onAppEvent: { event: 'habitat:tab-changed', targetStepId: STEP.FEED_BRANCH },
    },
  },

  {
    id: STEP.VM,
    analytics: { stepNumber: -1, section: 'Video demo' },
    ui: { modal: { kind: 'video', src: VIDEO_DEMO_SRC, onCloseStepId: STEP.WELCOME } },
  },

  // Already following: just point at the feed. Not following: also offer to be notified.
  {
    id: STEP.FEED_BRANCH,
    analytics: { stepNumber: -1 },
    ui: {
      autoAdvance: [
        { cond: 'isFollowingHabitat', delayMs: 0, targetStepId: STEP.FEED_FOLLOWS },
        { delayMs: 0, targetStepId: STEP.FEED_NO_FOLLOW },
      ],
    },
  },

  {
    id: STEP.FEED_FOLLOWS,
    analytics: { stepNumber: 12, section: 'Feed' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble: 'The community keeps tabs — check where they were last spotted.',
        buttons: [{ label: 'Who else is out and about?', targetStepId: TAIL_STEP_IDS.NEXT_HABITAT }],
      },
      spotlight: { target: TOUR_TARGETS.ACTIVITY_FEED, scrollIntoView: true },
    },
  },

  {
    id: STEP.FEED_NO_FOLLOW,
    analytics: { stepNumber: 12, section: 'Feed' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble:
          'The community keeps tabs — check the Feed for where they were last seen, or hop somewhere new and ' +
          "see who's out and about.",
        buttons: [
          { label: 'Who else is out and about?', targetStepId: TAIL_STEP_IDS.NEXT_HABITAT },
          { label: 'When are they back?', targetStepId: STEP.FOLLOW_ALT },
        ],
      },
      spotlight: { target: TOUR_TARGETS.ACTIVITY_FEED, scrollIntoView: true },
    },
  },

  {
    id: STEP.FOLLOW_ALT,
    analytics: { stepNumber: 13, section: 'Follow' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble: "Click on Follow, and you'll get a ping when they're back on camera live.",
      },
      spotlight: { target: TOUR_TARGETS.FOLLOW, scrollIntoView: true },
      onAppEvent: { event: 'habitat:followed', targetStepId: TAIL_STEP_IDS.NEXT_HABITAT },
      // Reached by a direct button, not the follow branch, so guard it here too: an
      // already-following user must not be asked to follow.
      autoAdvance: { cond: 'isFollowingHabitat', delayMs: 0, targetStepId: TAIL_STEP_IDS.NEXT_HABITAT },
    },
  },

  ...habitatTailSteps(),
];

export const habitatOutageConfig: TourConfig = { id: HABITAT_ONBOARDING_OUTAGE_ID, steps };
export const OUTAGE_FIRST_STEP_ID = STEP.WELCOME;
